import { BASE_URL } from '@avanta/shared/constants/common';
import { service, type ServiceRequestConfig } from '@avanta/shared/services/api';
import {
  oauthToken,
  getAnonymousToken,
  loginWithPassword,
  refreshAccessToken,
  logout as logoutShared,
  recoverPassword,
  verifyRecoveryCode,
  resendRecoveryCode,
  changePassword,
  changeUserPassword,
} from '@avanta/shared/services/auth';
import { useChatStore } from '@avanta/shared/stores/chat/index';
import type { AuthInfo } from '@avanta/shared/types/auth';
import { type User } from '@avanta/shared/types/users';
import { clearRecoveryCookies } from '@avanta/shared/utils/auth';
import { LOGIN_ERROR_CODE, LoginAuthError } from '@avanta/shared/utils/login-errors';
import { type AxiosResponse } from 'axios';
import { queryClient } from '@/query-manager';
import { AcceptedUsers } from 'constants/auth';
import { ROUTES } from 'constants/routes';
import { COOKIES } from 'constants/storage';
import { useUserStore } from 'stores/user';
import { getAnonymousTokenCookie, setCookieWithoutExpiry } from 'utils/storage';
import { type AuthServiceTypes, type SignupParams, type SignupResponse } from './types';

const loginWithMagicLink = async (
  token: string,
  username: string
): Promise<AxiosResponse<User>> => {
  const config: ServiceRequestConfig = {
    params: { email: username },
    headers: {
      Authorization: `Bearer ${token}`,
    },
    skipGlobalErrorHandler: true,
  };
  return service.post(`${BASE_URL}users/login/by-email`, {}, config);
};

const login = async (
  username: string,
  password: string,
  magicToken?: string
): Promise<AuthInfo> => {
  let token;
  try {
    token = await oauthToken(username, password, magicToken);
  } catch {
    throw new LoginAuthError(LOGIN_ERROR_CODE.INVALID_CREDENTIALS);
  }

  if (!token) {
    throw new LoginAuthError(LOGIN_ERROR_CODE.INVALID_CREDENTIALS);
  }

  let user;
  try {
    user = magicToken
      ? await loginWithMagicLink(token.data.access_token, username)
      : await loginWithPassword(username, password);
  } catch {
    throw new LoginAuthError(LOGIN_ERROR_CODE.RECOVER_PASSWORD);
  }

  const isAcceptedType = AcceptedUsers.includes(user.data.type as (typeof AcceptedUsers)[number]);

  if (!isAcceptedType) {
    await logout(false);
    throw new LoginAuthError(LOGIN_ERROR_CODE.INVALID_CREDENTIALS);
  }

  setCookieWithoutExpiry(COOKIES.REFRESH_TOKEN, token.data.refresh_token);
  useUserStore.getState().setUser(user.data);
  setCookieWithoutExpiry(COOKIES.USER_ID, user.data.id);
  clearRecoveryCookies();

  if (user.data.requiredProfileSetup) {
    window.location.href = ROUTES.PROFILE_SETUP;
    return {
      ...token.data,
      user: user.data,
    };
  }

  return {
    user: user.data,
    access_token: token.data.access_token,
    expires_in: token.data.expires_in,
    refresh_token: token.data.refresh_token,
    scope: token.data.scope,
    token_type: token.data.token_type,
  };
};

const logout = async (redirect = true): Promise<void> => {
  await useChatStore.getState().disconnectAndReset();
  queryClient.clear();
  logoutShared(redirect);
};

const signup = async (params: SignupParams): Promise<AxiosResponse<SignupResponse>> => {
  const anonymousToken =
    getAnonymousTokenCookie() ?? (await getAnonymousToken())?.data.access_token;

  const isPatient = params.body.type !== 'EmployeeToRegisterDTO' && !params.body.enterpriseId;
  const registerUrl = isPatient ? `${BASE_URL}patients` : `${BASE_URL}employees`;

  try {
    const response = await service.post(registerUrl, params.body, {
      headers: {
        Authorization: `Bearer ${anonymousToken}`,
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const requestMagicLink = async (email: string): Promise<AxiosResponse<void>> => {
  const anonymousToken = await getAnonymousToken();
  return service.post(
    `${BASE_URL}auth/magic/request`,
    {},
    {
      params: { email },
      headers: {
        Authorization: `Bearer ${anonymousToken?.data.access_token}`,
      },
    }
  );
};

export const AuthService: AuthServiceTypes = {
  login: async (username: string, password: string, magicToken?: string) =>
    login(username, password, magicToken),
  refreshAccessToken: async (refreshToken: string) => refreshAccessToken(refreshToken),
  logout,
  signup,
  requestMagicLink,
  recoverPassword,
  verifyRecoveryCode,
  resendRecoveryCode,
  changePassword,
  changeUserPassword,
};
